import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { loginSuccess } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [handle, setHandle] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email, password } : { email, password, handle: handle.trim() || undefined };
      const { data } = await api.post(path, payload);
      loginSuccess(data);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#FDFBF7]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-full bg-[#8A9A86] flex items-center justify-center">
              <Leaf size={22} weight="fill" color="#fff" />
            </div>
            <span className="font-heading text-2xl font-semibold text-[#2C362B]">The Safe Space</span>
          </Link>

          {/* Mobile-only hero: shows the cloud image + quote on small screens */}
          <div className="lg:hidden relative rounded-3xl overflow-hidden mb-8 h-36" data-testid="mobile-auth-hero">
            <img
              src="https://images.unsplash.com/photo-1547963802-25f153e14080?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
              alt="Soft pastel sky"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#2C362B]/35" />
            <p className="absolute bottom-4 left-4 right-4 font-heading text-lg font-medium text-white drop-shadow-md leading-snug">
              "You don't have to carry it alone. Speak freely, stay anonymous."
            </p>
          </div>

          <h1 className="font-heading text-4xl font-semibold text-[#2C362B] mb-2">
            {isLogin ? "Welcome back" : "Find your calm"}
          </h1>
          <p className="text-[#6D7A6A] mb-8 leading-relaxed">
            {isLogin
              ? "A quiet, anonymous space to share whatever you're carrying."
              : "Create an account and get a private anonymous handle. No one ever sees your real identity."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold tracking-[0.15em] uppercase text-[#8A9A86]">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 w-full bg-[#F4F1E8] border-none rounded-2xl px-5 py-4 text-[#2C362B] placeholder:text-[#A8B0A3] focus:ring-2 focus:ring-[#8A9A86] focus:bg-white outline-none transition-all duration-300"
                data-testid="email-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.15em] uppercase text-[#8A9A86]">Password</label>
              <div className="relative mt-2">
                <input
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isLogin ? "Your password" : "At least 6 characters"}
                  className="w-full bg-[#F4F1E8] border-none rounded-2xl px-5 py-4 pr-12 text-[#2C362B] placeholder:text-[#A8B0A3] focus:ring-2 focus:ring-[#8A9A86] focus:bg-white outline-none transition-all duration-300"
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A9A86]">
                  {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {isLogin && (
                <div className="text-right mt-2">
                  <Link to="/forgot-password" className="text-sm font-semibold text-[#6D7A6A] hover:text-[#5E6C5C] hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <label className="text-xs font-bold tracking-[0.15em] uppercase text-[#8A9A86]">Anonymous name</label>
                <input
                  type="text"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  maxLength={30}
                  placeholder="e.g. Quiet Fox (leave blank for a random one)"
                  className="mt-2 w-full bg-[#F4F1E8] border-none rounded-2xl px-5 py-4 text-[#2C362B] placeholder:text-[#A8B0A3] focus:ring-2 focus:ring-[#8A9A86] focus:bg-white outline-none transition-all duration-300"
                  data-testid="handle-input"
                />
                <p className="text-xs text-[#A8B0A3] mt-2">This is the only name others see. Your email always stays private. You can change it anytime.</p>
              </div>
            )}

            {error && (
              <p className="text-sm text-[#D97B6A] bg-[#FBEAE7] rounded-xl px-4 py-3" data-testid="auth-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#8A9A86] text-white hover:bg-[#6D7A6A] rounded-full px-8 py-4 font-semibold transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="auth-submit-btn"
            >
              {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-8 text-center text-[#6D7A6A] text-sm">
            {isLogin ? "New here? " : "Already have a space? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-[#5E6C5C] font-bold hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </motion.div>
      </div>

      <div className="hidden lg:block flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1547963802-25f153e14080?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Soft pastel sky"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-white/10" />
        <div className="absolute bottom-16 left-12 right-12">
          <p className="font-heading text-3xl font-medium text-white drop-shadow-lg leading-snug">
            "You don't have to carry it alone. Speak freely, stay anonymous."
          </p>
        </div>
      </div>
    </div>
  );
}
